import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { supabase } from "utils/supabase";

const RecentSubmissionsTable = ({
  companyOptions,
  submissions,
  onEdit,
  onDelete,
}) => {
  const [sortField, setSortField] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filterCompany, setFilterCompany] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // const companyOptions = [
  //   { value: "all", label: "All Companies" },
  //   { value: "acme-corp", label: "Acme Corporation" },
  //   { value: "tech-solutions", label: "Tech Solutions Inc." },
  //   { value: "global-enterprises", label: "Global Enterprises" },
  //   { value: "innovation-labs", label: "Innovation Labs" },
  //   { value: "future-systems", label: "Future Systems" },
  //   { value: "digital-dynamics", label: "Digital Dynamics" },
  //   { value: "smart-solutions", label: "Smart Solutions Ltd." },
  //   { value: "nexus-technologies", label: "Nexus Technologies" },
  // ];

  const getCompanyName = (companyValue) => {
    const company = companyOptions.find((c) => c.value === companyValue);

    return company ? company.label : companyValue;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getCollectionRateColor = (rate) => {
    if (rate >= 90) return "text-success";
    if (rate >= 70) return "text-warning";
    return "text-error";
  };

  // Filter and search submissions
  const filteredSubmissions = submissions.filter((submission) => {
    const matchesCompany =
      filterCompany === "all" || submission?.company_id === filterCompany;
    const matchesSearch =
      searchTerm == "" ||
      getCompanyName(submission?.company_id || null)
        .toLowerCase()
        .includes(searchTerm?.toLowerCase()) ||
      submission?.notes?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCompany && matchesSearch;
  });

  // Sort submissions
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === "company_id") {
      aValue = getCompanyName(a.company_id);
      bValue = getCompanyName(b.company_id);
    }

    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Paginate submissions
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubmissions = sortedSubmissions.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "ArrowUpDown";
    return sortDirection === "asc" ? "ArrowUp" : "ArrowDown";
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Recent Submissions
          </h3>
          <p className="text-sm text-muted-foreground">
            Audit trail of all data entries with modification history
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredSubmissions.length} entries
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            placeholder="Search by company or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            iconName="Search"
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            placeholder="Filter by company"
            options={companyOptions}
            value={filterCompany}
            onChange={setFilterCompany}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort("company_id")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Company</span>
                  <Icon name={getSortIcon("company_id")} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-4 ">
                <button
                  onClick={() => handleSort("entry_date")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Date</span>
                  <Icon name={getSortIcon("entry_date")} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-4 ">
                <button
                  onClick={() => handleSort("sales_amount")}
                  className="flex items-center justify-end space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Sales</span>
                  <Icon name={getSortIcon("sales_amount")} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-4 ">
                <button
                  onClick={() => handleSort("collection_amount")}
                  className="flex items-center justify-end space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Collection</span>
                  <Icon name={getSortIcon("collection_amount")} size={14} />
                </button>
              </th>
              <th className=" py-3 px-4 text-left">
                <button
                  onClick={() => handleSort("collection_rate")}
                  className="flex items-center justify-end space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Rate</span>
                  <Icon name={getSortIcon("collection_rate")} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort("updated_at")}
                  className="flex items-center justify-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Submitted</span>
                  <Icon name={getSortIcon("updated_at")} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort("user_name")}
                  className="flex items-center justify-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>User Name</span>
                  <Icon name={getSortIcon("user_name")} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <span className="text-sm font-medium text-left">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedSubmissions.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-left py-8">
                  <Icon
                    name="FileText"
                    size={32}
                    className="text-muted-foreground mx-auto mb-2"
                  />
                  <p className="text-sm text-muted-foreground">
                    No submissions found
                  </p>
                </td>
              </tr>
            ) : (
              paginatedSubmissions.map((submission, index) => {
                const collectionRate =
                  submission.salesAmount > 0
                    ? (submission.collectionAmount / submission.salesAmount) *
                      100
                    : 0;
                const isEvenRow = index % 2 === 0;

                return (
                  <tr
                    key={submission.id}
                    className={`border-b border-border hover:bg-muted/50 transition-colors duration-150 ${
                      isEvenRow ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-left">
                          {getCompanyName(submission.company_id)}
                        </span>
                        {submission.notes && (
                          <span className="text-xs text-muted-foreground truncate max-w-32">
                            {submission.notes}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-left">
                        {new Date(submission.entry_date).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-left">
                      <span className="text-sm font-medium text-foreground">
                        {formatCurrency(submission.sales_amount)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-left">
                      <span className="text-sm font-medium text-foreground">
                        {formatCurrency(submission.collection_amount)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-left">
                      <span
                        className={`text-sm font-medium ${getCollectionRateColor(
                          submission.collection_rate
                        )}`}
                      >
                        {submission.collection_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-left">
                      <span className="text-sm ">
                        {new Date(submission.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-left">
                      <span className="text-sm ">{submission.user_name}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-start space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(submission)}
                          iconName="Edit2"
                          className="h-8 w-8 p-0"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(submission.id)}
                          iconName="Trash2"
                          className="h-8 w-8 p-0 text-error hover:text-error"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to{" "}
            {Math.min(startIndex + itemsPerPage, filteredSubmissions.length)} of{" "}
            {filteredSubmissions.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
              className="h-8 w-8 p-0"
            />
            <span className="text-sm text-foreground px-3">
              {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
              className="h-8 w-8 p-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentSubmissionsTable;
